"use client"

import { useState, useRef } from "react"
import { useForm } from "react-hook-form"
import { zodResolver } from "@hookform/resolvers/zod"
import { contactFormSchema, ContactFormValues } from "../schemas/contact-form-schema"
import { submitContactFormAction } from "../actions"
import { toast } from "@shared/ui"

export function useContactForm() {
  const [files, setFiles] = useState<File[]>([])
  const [progress, setProgress] = useState<{ [key: string]: number }>({})
  const inputRef = useRef<HTMLInputElement | null>(null)

  const {
    control,
    handleSubmit,
    setValue,
    reset,
    formState: { errors },
  } = useForm<ContactFormValues>({
    resolver: zodResolver(contactFormSchema),
    defaultValues: { files: [], module: "", email: "", message: "" },
  })

  const addFiles = (newFiles: File[]) => {
    setFiles((prev) => {
      const existingFileNames = prev.map(file => file.name)
      const uniqueNewFiles = newFiles.filter(file => !existingFileNames.includes(file.name))
      
      if (uniqueNewFiles.length === 0) {
        return prev
      }
      
      const updatedFiles = [...prev, ...uniqueNewFiles]
      setValue("files", updatedFiles)
      simulateProgress(uniqueNewFiles)
      return updatedFiles
    })
  }

  const onDrop = (event: React.DragEvent<HTMLDivElement>) => {
    event.preventDefault()
    const droppedFiles = Array.from(event.dataTransfer.files)
    addFiles(droppedFiles)
  }

  const onFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const selectedFiles = event.target.files ? Array.from(event.target.files) : []
    addFiles(selectedFiles)
    if (event.target) {
      event.target.value = ""
    }
  }

  const removeFile = (fileName: string) => {
    setFiles((prev) => {
      const updatedFiles = prev.filter(file => file.name !== fileName)
      setValue("files", updatedFiles)
      return updatedFiles
    })
    
    setProgress((prev) => {
      const { [fileName]: removed, ...rest } = prev
      return rest
    })
  }

  const simulateProgress = (newFiles: File[]) => {
    newFiles.forEach((file) => {
      let percent = 0
      const interval = setInterval(() => {
        percent += Math.random() * 20 + 10
        setProgress((prev) => ({ ...prev, [file.name]: Math.min(percent, 100) }))
        if (percent >= 100) clearInterval(interval)
      }, 300)
    })
  }

  const handleUploadClick = () => {
    inputRef.current?.click()
  }

  const onSubmit = async (data: ContactFormValues) => {
    try {
      const result = await submitContactFormAction(data)
      
      if (result.success) {
        toast.success("Message sent successfully!")
        setFiles([])
        setProgress({})
        reset()
      } else {
        toast.error(`Error sending message: ${result.error}`)
      }
    } catch (error) {
      console.error("Error submitting form:", error)
      toast.error("An unexpected error occurred. Please try again.")
    }
  }

  return {
    files,
    progress,
    inputRef,
    control,
    handleSubmit,
    reset,
    errors,
    onDrop,
    onFileChange,
    handleUploadClick,
    removeFile,
    onSubmit,
  }
} 